import React, { useState } from 'react';
import {
    Box,
    Card,
    Typography,
    Stack,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    TextField,
    Chip,
    Avatar
} from '@mui/material';
import { Visibility, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useKycs } from 'hooks/useKyc';
import { format } from 'date-fns';

const KycReviewBoard = () => {
    const navigate = useNavigate();
    const { data: kycs, isLoading } = useKycs();
    const [searchTerm, setSearchTerm] = useState('');

    const filteredKycs = (kycs || []).filter((kyc) => {
        if (!searchTerm) return true;
        const nameMatch =
            kyc.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            kyc.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
        const emailMatch = kyc.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const docMatch = kyc.identity?.documentNumber?.includes(searchTerm);
        return nameMatch || emailMatch || docMatch;
    });

    const getStatusChip = (status) => {
        switch (status) {
            case 'pending':
                return <Chip label="Pending Review" color="warning" size="small" />;
            case 'approved':
                return <Chip label="Approved" color="success" size="small" />;
            case 'rejected':
                return <Chip label="Rejected" color="error" size="small" />;
            default:
                return <Chip label="Not Started" color="default" size="small" />;
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={{ xs: 2, md: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={800} color="primary.main">
                    KYC Review Board
                </Typography>
            </Stack>

            <Card sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Search by vendor name, email or document number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        )
                    }}
                />
            </Card>

            <Card sx={{ p: 2, borderRadius: 3 }}>
                <TableContainer component={Paper} elevation={0}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <TableCell>
                                    <strong>Applicant</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Type</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Submitted On</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Overall Status</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredKycs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        No KYC applications found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredKycs.map((kyc) => (
                                    <TableRow key={kyc._id} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Avatar src={kyc.user?.profilePicture}>{kyc.user?.firstName?.charAt(0)}</Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {kyc.user?.firstName} {kyc.user?.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {kyc.user?.email}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                {kyc.userType || 'Vendor / Rider'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{format(new Date(kyc.createdAt), 'MMM dd, yyyy')}</TableCell>
                                        <TableCell>{getStatusChip(kyc.status)}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<Visibility />}
                                                onClick={() => navigate(`/admin/kyc/${kyc.user?._id}`)}
                                                color={kyc.status === 'pending' ? 'primary' : 'inherit'}
                                            >
                                                Review
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
};

export default KycReviewBoard;
